import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

// Shared 3-step toon gradient for cel shading.
let GRAD = null;
function toonGradient() {
  if (GRAD) return GRAD;
  const data = new Uint8Array([80, 80, 80, 255, 160, 160, 160, 255, 255, 255, 255, 255]);
  const tex = new THREE.DataTexture(data, 3, 1, THREE.RGBAFormat);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  GRAD = tex;
  return tex;
}

const SKIN = 0xe8b98f;

// Mario-Kart-style low-poly, toon-shaded human. pose: stand | walk | climb | lie
export default function ToonNPC({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  shirt = 0xd23b3b,
  pants = 0x2b4c8c,
  pose = "walk",
  speed = 6,
  scale = 1,
  getRun,
}) {
  const grad = toonGradient();
  const grp = useRef();
  const armL = useRef();
  const armR = useRef();
  const legL = useRef();
  const legR = useRef();
  const offset = useMemo(() => Math.random() * 10, []);

  const skinMat = useMemo(() => new THREE.MeshToonMaterial({ color: SKIN, gradientMap: grad }), [grad]);
  const shirtMat = useMemo(() => new THREE.MeshToonMaterial({ color: shirt, gradientMap: grad }), [shirt, grad]);
  const pantsMat = useMemo(() => new THREE.MeshToonMaterial({ color: pants, gradientMap: grad }), [pants, grad]);
  const hairMat = useMemo(() => new THREE.MeshToonMaterial({ color: 0x2a1c14, gradientMap: grad }), [grad]);
  const eyeMat = useMemo(() => new THREE.MeshBasicMaterial({ color: 0x141414 }), []);

  useFrame((state) => {
    if (!grp.current) return;
    // dynamic sprint: on the collapse run every figure breaks into a full sprint
    const runNow = (getRun && getRun() > 0.05) || pose === "run";
    if (pose === "lie") return;
    if (runNow) {
      const t = state.clock.elapsedTime * Math.max(7, speed) + offset;
      const legSwing = Math.sin(t) * 1.1;   // long, fast strides
      const armSwing = Math.sin(t) * 0.85;  // counter-swinging arms
      if (armL.current) armL.current.rotation.x = -armSwing;
      if (armR.current) armR.current.rotation.x = armSwing;
      if (legL.current) legL.current.rotation.x = legSwing;
      if (legR.current) legR.current.rotation.x = -legSwing;
      grp.current.position.y = Math.abs(Math.sin(t)) * 0.12; // head/body bob
      grp.current.rotation.x = 0.26;                          // forward sprint lean
      grp.current.rotation.z = Math.sin(t) * 0.04;            // slight torso roll
      return;
    }
    grp.current.rotation.x = 0;
    grp.current.rotation.z = 0;
    // somber pass: cap cadence so every background figure moves at a heavy, exhausted pace
    const somber = Math.min(speed, 2.2);
    const t = state.clock.elapsedTime * somber + offset;
    const active = pose === "walk" || pose === "climb";
    const swing = active ? Math.sin(t) * (pose === "climb" ? 0.5 : 0.32) : Math.sin(t * 0.3) * 0.04;
    if (armL.current) armL.current.rotation.x = swing;
    if (armR.current) armR.current.rotation.x = -swing;
    if (legL.current) legL.current.rotation.x = -swing;
    if (legR.current) legR.current.rotation.x = swing;
    grp.current.position.y = active ? Math.abs(Math.sin(t)) * 0.025 : 0;
  });

  const baseRot = pose === "lie" ? [Math.PI / 2, rotation[1], rotation[2]] : (pose === "climb" ? [0.28, rotation[1], rotation[2]] : rotation);

  return (
    <group position={position} rotation={baseRot} scale={scale}>
      <group ref={grp}>
        {/* torso */}
        <mesh material={shirtMat} position={[0, 0.92, 0]}>
          <capsuleGeometry args={[0.17, 0.34, 4, 8]} />
        </mesh>
        {/* hips */}
        <mesh material={pantsMat} position={[0, 0.66, 0]}>
          <boxGeometry args={[0.32, 0.16, 0.22]} />
        </mesh>
        {/* head */}
        <mesh material={skinMat} position={[0, 1.34, 0]}>
          <sphereGeometry args={[0.16, 10, 8]} />
        </mesh>
        <mesh material={hairMat} position={[0, 1.4, -0.01]}>
          <sphereGeometry args={[0.165, 10, 6, 0, Math.PI * 2, 0, Math.PI / 1.7]} />
        </mesh>
        <mesh material={eyeMat} position={[-0.06, 1.35, 0.14]}><sphereGeometry args={[0.022, 6, 6]} /></mesh>
        <mesh material={eyeMat} position={[0.06, 1.35, 0.14]}><sphereGeometry args={[0.022, 6, 6]} /></mesh>
        {/* arms (pivot at shoulder) */}
        <group ref={armL} position={[-0.22, 1.12, 0]}>
          <mesh material={shirtMat} position={[0, -0.2, 0]}><capsuleGeometry args={[0.06, 0.3, 4, 6]} /></mesh>
          <mesh material={skinMat} position={[0, -0.42, 0]}><sphereGeometry args={[0.06, 6, 6]} /></mesh>
        </group>
        <group ref={armR} position={[0.22, 1.12, 0]}>
          <mesh material={shirtMat} position={[0, -0.2, 0]}><capsuleGeometry args={[0.06, 0.3, 4, 6]} /></mesh>
          <mesh material={skinMat} position={[0, -0.42, 0]}><sphereGeometry args={[0.06, 6, 6]} /></mesh>
        </group>
        {/* legs (pivot at hip) */}
        <group ref={legL} position={[-0.09, 0.6, 0]}>
          <mesh material={pantsMat} position={[0, -0.26, 0]}><capsuleGeometry args={[0.07, 0.34, 4, 6]} /></mesh>
          <mesh material={hairMat} position={[0, -0.5, 0.04]}><boxGeometry args={[0.1, 0.07, 0.2]} /></mesh>
        </group>
        <group ref={legR} position={[0.09, 0.6, 0]}>
          <mesh material={pantsMat} position={[0, -0.26, 0]}><capsuleGeometry args={[0.07, 0.34, 4, 6]} /></mesh>
          <mesh material={hairMat} position={[0, -0.5, 0.04]}><boxGeometry args={[0.1, 0.07, 0.2]} /></mesh>
        </group>
      </group>
    </group>
  );
}

export const NPC_PALETTE = [
  { shirt: 0xd23b3b, pants: 0x2b3a55 },
  { shirt: 0x2e8b57, pants: 0x3a3a3a },
  { shirt: 0x3f6fd6, pants: 0x22303f },
  { shirt: 0xe0a92b, pants: 0x4a3520 },
  { shirt: 0x8e4fbf, pants: 0x2a2a2a },
  { shirt: 0xcf5a2a, pants: 0x33475b },
];
